import React, { useCallback, useContext, useState } from 'react';
import { Button, Col } from 'react-bootstrap';
import { AttachMoney, Send } from '@material-ui/icons';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LinkIcon from '@material-ui/icons/Link';
import IconButton from '@material-ui/core/IconButton';
import { navigate } from '@reach/router';

import { testIds, generateTestId } from 'common/testIds';
import PermissionsGate from 'components/shared/PermissionsGate';
import strings from 'common/strings';
import permissions from 'common/permissions';
import { useWindowSize } from 'hooks';
import { sendMessageToExtension } from 'utils/extension';
import { vehicleDetailsBuilder } from 'navigation/routes';
import { getFormattedDurationFromSeconds } from 'utils/time';
import PriceHistoryModal from 'components/shared/PriceHistoryModal';
import useEngageOverview from 'api/inventory/useEngageOverview';
import { getExtensionUrlParams } from 'common/urlParams';
import SendVehicleModal from 'components/shared/SendVehicleModal';
import { VehicleSummary } from 'models';
import { VehicleCardContext } from '../../VehicleCard';

const engagePlaceholderTitles = [
  'SHOPPERS',
  'PORTFOLIOS SENT',
  'PORTFOLIO OPENS',
  'OPEN RATE',
  'ENGAGEMENTS',
  'AVG. ENGAGEMENT TIME',
];

const extensionUrlParams = getExtensionUrlParams();

const TabButtonsRow = (
  handleClickNav: (clickSource: string, engage?: boolean) => void,
  openPriceHistoryModal: () => void,
  position: number
) => {
  const { vehicle } = useContext(VehicleCardContext);
  const windowSize = useWindowSize();
  const [isSendVehicleModalOpen, setIsSendVehicleModalOpen] = useState(false);

  const handleEngageSendClick = (vehicle: VehicleSummary | undefined) => {
    if (vehicle) {
      setIsSendVehicleModalOpen(true);
    }
  };

  const EngageButtons = () => {
    return (
      <>
        <Button
          as="button"
          className={`${generateTestId(
            testIds.INVENTORY_CARD_ENGAGE_BUTTON_PRICE,
            {
              position: position.toString(),
            }
          )} engage-buttons`}
          variant="outline-dark"
          onClick={openPriceHistoryModal}
        >
          <span>{strings.PRICE}</span>
          <AttachMoney className="engage-btn-icon" />
        </Button>
        <PermissionsGate permissions={[permissions.PLUGIN_VELOCITYENGAGE_SEND]}>
          <Button
            as="button"
            className={`${generateTestId(
              testIds.INVENTORY_CARD_ENGAGE_BUTTON_SEND,
              {
                position: position.toString(),
              }
            )} engage-buttons`}
            variant="outline-dark"
            onClick={() => handleEngageSendClick(vehicle)}
          >
            <span>{strings.SEND}</span>
            <Send className="engage-btn-icon" />
          </Button>
          <SendVehicleModal
            selectedVehicleList={vehicle ? [vehicle] : []}
            open={isSendVehicleModalOpen}
            onClose={() => setIsSendVehicleModalOpen(false)}
          />
        </PermissionsGate>
      </>
    );
  };

  if (windowSize.width < 1350) {
    return (
      <div className="VehicleCard-step-buttons-container d-flex">
        <div className="d-flex engage-button-col">{EngageButtons()}</div>
        <Col className="px-0 pl-2">
          <Button
            variant="outline-primary"
            block
            onClick={() =>
              handleClickNav('VehicleCard-view-more-container)', true)
            }
          >
            {strings.VIEW_VEHICLE.toUpperCase()}
          </Button>
        </Col>
      </div>
    );
  }

  return (
    <>
      <Button
        as="button"
        className="see-more-button"
        variant="link"
        onClick={() => handleClickNav('VehicleCard-view-more-container', true)}
      >
        <span>{strings.VIEW_MORE}</span>
        <ChevronRightIcon />
      </Button>
      <div className="d-flex">{EngageButtons()}</div>
    </>
  );
};

const VehicleCardEngageDetails = () => {
  const {
    vehicle,
    inExtensionCardMode,
    position,
    currentTab: activeCollectionType,
  } = useContext(VehicleCardContext);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCopyPopupOpen, setIsCopyPopupOpen] = useState<boolean>(false);
  const { refetch: refetchEngageOverview } = useEngageOverview(
    vehicle?.vehicleCard?.id,
    false
  );

  const openPriceHistoryModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const renderEngageValues = useCallback(() => {
    const overviewCards = vehicle?.vehicleCard?.velocityEngageOverviewCards;
    if (!overviewCards) return undefined;

    if (overviewCards?.length === 0) {
      return engagePlaceholderTitles.map((placeholderTitle, idx) => (
        <div
          key={`${placeholderTitle}-${idx}`}
          className="VehicleCard-tabs-row-inventory-item"
        >
          <div className="VehicleCard-label">{placeholderTitle}</div>
          <div
            className="VehicleCard-value font-weight-600"
            data-vas-testing={generateTestId(
              testIds.INVENTORY_CARD_ENGAGE_VALUE,
              {
                position: position.toString(),
                label: `${placeholderTitle}`,
              }
            )}
          >
            {strings.EMPTY_VALUE}
          </div>
        </div>
      ));
    }

    return overviewCards.map((card) => {
      let convertedValue: string | number | null = null;
      if (card.name === 'AVG_SESSION_DURATION_SECONDS') {
        convertedValue = getFormattedDurationFromSeconds(Number(card.value));
      } else if (card.name === 'FOLIO_OPEN_PERCENTAGE') {
        // rounding a server side value to two decimal percent (0.0492 to 4.92% for example)
        const percent = Math.floor(Number(card.value) * 10000) / 100;
        convertedValue = percent ? `${percent}%` : strings.EMPTY_VALUE;
      }

      return (
        <div
          key={`${card.name}-${card.id}`}
          className="VehicleCard-tabs-row-inventory-item"
        >
          <div className="VehicleCard-label">{card.displayName}</div>
          <div
            className="VehicleCard-value font-weight-600"
            data-vas-testing={generateTestId(
              testIds.INVENTORY_CARD_ENGAGE_VALUE,
              {
                position: position.toString(),
                label: `${card.displayName}`,
              }
            )}
          >
            {convertedValue || card.value || strings.EMPTY_VALUE}
          </div>
        </div>
      );
    });
  }, [position, vehicle?.vehicleCard?.velocityEngageOverviewCards]);

  const handleClickNav = (clickSource: string, engage = false) => {
    if (vehicle?.vehicleCard?.id) {
      if (engage) {
        navigate(
          `${vehicleDetailsBuilder(
            vehicle?.vehicleCard?.id
          )}/engage/?fromVDP=${activeCollectionType}`
        );
      } else {
        navigate(
          `${vehicleDetailsBuilder(
            vehicle?.vehicleCard?.id
          )}?fromVDP=${activeCollectionType}`
        );
      }
    }
  };

  const handleClickEngageLink = async () => {
    if (
      !vehicle?.vehicleCard?.vin ||
      !vehicle.vehicleCard?.id ||
      !extensionUrlParams?.sourceId
    ) {
      return;
    }
    const { data: engageOverviewResponse, isError: isErrorEngageOverview } =
      await refetchEngageOverview();
    if (engageOverviewResponse?.data && !isErrorEngageOverview) {
      let shareUrl = engageOverviewResponse?.data.folioUrl;
      if (shareUrl) {
        shareUrl = shareUrl.replace(
          vehicle.vehicleCard.vin,
          vehicle.vehicleCard.id
        );
      }
      sendMessageToExtension(extensionUrlParams.sourceId, {
        action: 'clipboard',
        method: 'copy',
        value: shareUrl,
      });
      setIsCopyPopupOpen(true);
      setTimeout(() => {
        setIsCopyPopupOpen(false);
      }, 2000);
    }
  };

  return (
    <>
      <div className="engage">
        <div
          className="VehicleCard-tabs-row d-flex mt-0 flex-wrap"
          data-vas-testing={generateTestId(
            testIds.INVENTORY_CARD_ENGAGE_CONTAINER,
            {
              position: position.toString(),
            }
          )}
        >
          {inExtensionCardMode && (
            <div className="VehicleCard-tabs-engage-link">
              <IconButton onClick={handleClickEngageLink} size="small">
                <LinkIcon />
              </IconButton>
              {isCopyPopupOpen && (
                <div className="VehicleCard-tabs-engage-link-popover">
                  URL Copied to Clipboard
                </div>
              )}
            </div>
          )}
          {renderEngageValues()}
        </div>
        {!inExtensionCardMode && (
          <div
            className="tab-buttons-row"
            data-vas-testing={generateTestId(
              testIds.INVENTORY_CARD_ENGAGE_BUTTONS_CONTAINER,
              {
                position: position.toString(),
              }
            )}
          >
            {TabButtonsRow(handleClickNav, openPriceHistoryModal, position)}
          </div>
        )}
      </div>
      {vehicle?.vehicleCard?.priceHistory && (
        <PriceHistoryModal
          open={isModalOpen}
          onClose={closeModal}
          onClick={openPriceHistoryModal}
          priceHistory={vehicle.vehicleCard.priceHistory}
          customButton
        />
      )}
    </>
  );
};

export default VehicleCardEngageDetails;
